import {
    CASE_CONTEXT_SCHEMA_KEY,
    CaseDocumentSchemaKey,
    DEVICE_PREPARATION_SCHEMA_KEY,
    FORM_ACTIONS_SCHEMA_KEY,
    INITIAL_EVALUATION_SCHEMA_KEY,
    POSITIONING_IMMOBILIZATION_SCHEMA_KEY,
    RISK_BENEFIT_SCHEMA_KEY,
} from './caseWorkspaceUtils';

export const DEFAULT_WORKSPACE_SCENARIO = 'default' as const;
export const RISK_BENEFIT_ONLY_SCENARIO = 'risk-benefit-only' as const;
export const POSITIONING_IMMOBILIZATION_ONLY_SCENARIO =
    'positioning-immobilization-only' as const;
export const DEVICE_PREPARATION_ONLY_SCENARIO =
    'device-preparation-only' as const;

export type WorkspaceScenarioKey =
    | typeof DEFAULT_WORKSPACE_SCENARIO
    | typeof RISK_BENEFIT_ONLY_SCENARIO
    | typeof POSITIONING_IMMOBILIZATION_ONLY_SCENARIO
    | typeof DEVICE_PREPARATION_ONLY_SCENARIO;

export interface WorkspaceDocumentFlow {
    schemaKey: CaseDocumentSchemaKey;
    dependsOn?: CaseDocumentSchemaKey[];
    ignoreVisibilityWhen?: boolean;
}

export interface WorkspaceScenarioConfig {
    key: WorkspaceScenarioKey;
    footerSchemaKey: typeof FORM_ACTIONS_SCHEMA_KEY;
    documentFlows: WorkspaceDocumentFlow[];
}

export const WORKSPACE_SCENARIOS: Record<
    WorkspaceScenarioKey,
    WorkspaceScenarioConfig
> = {
    [DEFAULT_WORKSPACE_SCENARIO]: {
        key: DEFAULT_WORKSPACE_SCENARIO,
        footerSchemaKey: FORM_ACTIONS_SCHEMA_KEY,
        documentFlows: [
            {
                schemaKey: CASE_CONTEXT_SCHEMA_KEY,
            },
            {
                schemaKey: INITIAL_EVALUATION_SCHEMA_KEY,
            },
            {
                schemaKey: RISK_BENEFIT_SCHEMA_KEY,
                dependsOn: [INITIAL_EVALUATION_SCHEMA_KEY],
            },
            {
                schemaKey: POSITIONING_IMMOBILIZATION_SCHEMA_KEY,
            },
            {
                schemaKey: DEVICE_PREPARATION_SCHEMA_KEY,
            },
        ],
    },
    [RISK_BENEFIT_ONLY_SCENARIO]: {
        key: RISK_BENEFIT_ONLY_SCENARIO,
        footerSchemaKey: FORM_ACTIONS_SCHEMA_KEY,
        documentFlows: [
            {
                schemaKey: CASE_CONTEXT_SCHEMA_KEY,
            },
            {
                schemaKey: RISK_BENEFIT_SCHEMA_KEY,
                ignoreVisibilityWhen: true,
            },
        ],
    },
    [POSITIONING_IMMOBILIZATION_ONLY_SCENARIO]: {
        key: POSITIONING_IMMOBILIZATION_ONLY_SCENARIO,
        footerSchemaKey: FORM_ACTIONS_SCHEMA_KEY,
        documentFlows: [
            {
                schemaKey: CASE_CONTEXT_SCHEMA_KEY,
            },
            {
                schemaKey: POSITIONING_IMMOBILIZATION_SCHEMA_KEY,
            },
        ],
    },
    [DEVICE_PREPARATION_ONLY_SCENARIO]: {
        key: DEVICE_PREPARATION_ONLY_SCENARIO,
        footerSchemaKey: FORM_ACTIONS_SCHEMA_KEY,
        documentFlows: [
            {
                schemaKey: CASE_CONTEXT_SCHEMA_KEY,
            },
            {
                schemaKey: DEVICE_PREPARATION_SCHEMA_KEY,
            },
        ],
    },
};

export const resolveWorkspaceScenarioKey = (
    scenarioQuery: string | null,
    workspaceModeQuery: string | null
): WorkspaceScenarioKey => {
    if (scenarioQuery === RISK_BENEFIT_ONLY_SCENARIO) {
        return RISK_BENEFIT_ONLY_SCENARIO;
    }

    if (scenarioQuery === POSITIONING_IMMOBILIZATION_ONLY_SCENARIO) {
        return POSITIONING_IMMOBILIZATION_ONLY_SCENARIO;
    }

    if (scenarioQuery === DEVICE_PREPARATION_ONLY_SCENARIO) {
        return DEVICE_PREPARATION_ONLY_SCENARIO;
    }

    if (workspaceModeQuery === '76016-only') {
        return RISK_BENEFIT_ONLY_SCENARIO;
    }

    return DEFAULT_WORKSPACE_SCENARIO;
};

export const getWorkspaceScenarioConfig = (
    scenarioKey: WorkspaceScenarioKey
): WorkspaceScenarioConfig => {
    return WORKSPACE_SCENARIOS[scenarioKey];
};
